import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import type { CampaignListItem } from './useCampaignList'
import * as styled from '../../../../util/styled'
import { Mail, Users, Unlink, Podcast, ChevronUp, Search, PieChart, Bookmark } from 'lucide-react'
import { Link } from 'react-router-dom'

import { useRouteById, useRouteMatchChecker } from '../../../../hooks/useRouterData'

const CampaignToggle = styled.component(DisclosureButton)('flex items-center gap-2 rounded-lg px-4 py-2 my-1 cursor-default text-brand font-semibold hover:bg-surface-darker w-full')
const makeIcon = (icon: typeof Mail) => styled.component(icon)('w-3 h-3 text-brand')
const makeToggleIcon = (icon: typeof Mail) => styled.component(icon)('w-3 h-3')
const UsersIcon = makeToggleIcon(Users)
const BrokenLinkIcon = makeToggleIcon(Unlink)
const PodcastIcon = makeToggleIcon(Podcast)
const FindJournalistsIcon = makeIcon(Search)
const BookmarkIcon = makeIcon(Bookmark)
const OverviewIcon = makeIcon(PieChart)
const ToggleIconWrapper = styled.div('flex items-center justify-center p-1 rounded-md')

const CampaignNavItem = styled.component(Link)<{ active: boolean }>((props) => ['flex items-center gap-2 rounded-lg pr-4 pl-8 py-2 first:-mt-1 last:mb-2 cursor-pointer text-brand text-sm font-semibold', {
  'bg-surface-darker': props.active,
  'hover:bg-surface-darker': !props.active
}])

const ToggleIcon = styled.component(ChevronUp)<{ open: boolean }>(({ open }) => ['w-3 h-3 animate-rotate', {
  'rotate-180': open,
  'rotate-90': !open,
}])


type CampaignProps = {
  campaign: CampaignListItem
  brandId: string
  active: boolean
  activeBrandId: string
}

export const Campaign = (props: CampaignProps) => {
  const { campaign, active, activeBrandId, brandId } = props
  const isRoute = useRouteMatchChecker()
  const campaignSettingsRoute = useRouteById('campaignSettings')
  const searchRoute = useRouteById('campaignSearch')
  const journalistsRoute = useRouteById('campaignJournalists')
  const Icon = {
    journalists: <UsersIcon />,
    broken_links: <BrokenLinkIcon />,
    podcasts: <PodcastIcon />,
  }[campaign.kind]
  const toggleColor = {
    journalists: 'bg-cyan-400',
    broken_links: 'bg-green-700 text-white',
    podcasts: 'bg-orange text-white',
  }[campaign.kind]

  return (
    <Disclosure defaultOpen={active}>
      {({ open }) => (
        <>
          <CampaignToggle>
            <ToggleIconWrapper className={toggleColor}>
              {Icon}
            </ToggleIconWrapper>
            {campaign.title}
            <ToggleIcon open={open} />
          </CampaignToggle>
          <DisclosurePanel transition className="origin-top transition duration-100 ease-out data-[closed]:-translate-y-4 data-[closed]:opacity-0">
            <CampaignNavItem active={active && isRoute('campaignSettings')} to={campaignSettingsRoute.toLink({ campaignId: campaign.id, brandId: activeBrandId })}>
              <OverviewIcon />
              Overview
            </CampaignNavItem>
            <CampaignNavItem active={active && isRoute('campaignSearch')} to={searchRoute.toLink({ brandId, campaignId: campaign.id })}> 
              <FindJournalistsIcon />
              Find More
            </CampaignNavItem>
            <CampaignNavItem active={active && isRoute('campaignJournalists')} to={journalistsRoute.toLink({ brandId, campaignId: campaign.id })}> 
              <BookmarkIcon />
              Saved
            </CampaignNavItem>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
};
